import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Clock, User } from 'lucide-react';

// Field name formatting map
const FIELD_LABELS = {
  dialed: 'Dialed',
  dialed_company_ivr: 'Company IVR',
  dialed_fd_phone: 'FD Phone',
  connected: 'Connected',
  connect_pct: 'Connect %',
  visit_scheduled: 'Visit Scheduled',
  visit_done: 'Visit Done',
  closure_expected: 'Closure Expected',
  revenue_expected: 'Revenue Expected',
  closure_phone: 'Closure (Phone)',
  closure_counter: 'Closure (Counter)',
  total_closure: 'Total Closure',
  closure_rate: 'Closure Rate',
  schedule_rate: 'Schedule Rate',
  visit_done_rate: 'Visit Done Rate',
  fd_availability: 'FD Availability',
  comments: 'Comments',
};

const formatFieldName = (key) => {
  return FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Format timestamp to IST
const formatTimestampIST = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const utcTimestamp = typeof timestamp === 'string' && !timestamp.endsWith('Z')
      ? timestamp + 'Z'
      : timestamp;
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) return '-';

    const options = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
    const day = parts.find(p => p.type === 'day').value;
    const month = parts.find(p => p.type === 'month').value;
    const year = parts.find(p => p.type === 'year').value;
    const hours = parts.find(p => p.type === 'hour').value;
    const minutes = parts.find(p => p.type === 'minute').value;

    return `${day}-${month}-${year} ${hours}:${minutes} IST`;
  } catch {
    return '-';
  }
};

const EditHistoryDialog = ({ open, onOpenChange, editHistory }) => {
  const history = editHistory && editHistory.length > 0
    ? [...editHistory].reverse() // Most recent first
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit History</DialogTitle>
        </DialogHeader>

        {history.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No edits recorded
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1" style={{ maxHeight: '65vh' }}>
            {history.map((edit, idx) => {
              const changes = edit.changes || {};
              const changeKeys = Object.keys(changes);

              return (
                <div key={idx} className="border rounded-lg p-4 bg-white shadow-sm">
                  {/* Edit header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        Edit #{edit.edit_number || (history.length - idx)}
                      </Badge>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock size={12} />
                        {formatTimestampIST(edit.edited_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <User size={12} />
                      {edit.edited_by || 'Unknown'}
                    </div>
                  </div>

                  {/* Edit reason */}
                  {edit.reason && (
                    <p className="text-sm italic text-gray-600 mb-3 bg-gray-50 p-2 rounded">
                      "{edit.reason}"
                    </p>
                  )}

                  {/* Changes table */}
                  {changeKeys.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1 pr-2 font-medium text-gray-500">Field</th>
                          <th className="text-center py-1 px-2 font-medium text-gray-500">Before</th>
                          <th className="text-center py-1 px-1 w-6"></th>
                          <th className="text-center py-1 pl-2 font-medium text-gray-500">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changeKeys.map((fieldKey) => {
                          const change = changes[fieldKey];
                          const oldVal = change?.old ?? change?.from ?? '-';
                          const newVal = change?.new ?? change?.to ?? '-';

                          return (
                            <tr key={fieldKey} className="border-b last:border-0">
                              <td className="py-1.5 pr-2 font-medium">{formatFieldName(fieldKey)}</td>
                              <td className="py-1.5 px-2 text-center">
                                <span className="text-red-600 line-through">{String(oldVal)}</span>
                              </td>
                              <td className="py-1.5 px-1 text-center">
                                <ArrowRight size={10} className="text-gray-400 inline" />
                              </td>
                              <td className="py-1.5 pl-2 text-center">
                                <span className="text-green-600 font-medium">{String(newVal)}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-gray-400">No field changes recorded</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditHistoryDialog;
